import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '@/src/store'
import type { FeatureVersion } from '@/src/types/featureExperience'
import { setFeaturePreference, selectResolvedFeature } from '@/src/store/apps/featureExperience'
import { isCutoverInstantReached } from '@/src/utils/featureExperienceDates'
import { isUserInFeatureRolloutAudience } from '@/src/utils/featureExperienceAudience'
import { isResolvedForced } from '@/src/utils/featureExperienceResolve'

export interface UseToggleFeatureResult {
  currentVersion: FeatureVersion
  toggle: () => Promise<void>
  isLoading: boolean
  error: string | null
}

export function useToggleFeature(
  featureKey: string,
  options?: { restaurantId?: string | number | null }
): UseToggleFeatureResult {
  const dispatch = useDispatch<AppDispatch>()
  const feature = useSelector((state: RootState) => selectResolvedFeature(featureKey)(state))
  const pending = useSelector((state: RootState) => state.featureExperience.setPreferencePending)
  const sliceError = useSelector((state: RootState) => state.featureExperience.error)
  const restoIdFromStore = useSelector((state: RootState) => state.gyg365RestoList?.restoData?.id)
  const effectiveRestaurantId = options?.restaurantId ?? restoIdFromStore ?? null

  const currentVersion: FeatureVersion = feature?.preferredVersion ?? feature?.resolvedVersion ?? 'classic'

  const toggle = useCallback(async () => {
    if (!feature || !isUserInFeatureRolloutAudience(feature)) {
      return
    }
    if (!feature.canToggle || isResolvedForced(feature)) {
      return
    }
    if (isCutoverInstantReached(feature.cutoverDate)) {
      return
    }
    if (feature.lockAfterOptInToNext && currentVersion === 'next') {
      return
    }
    const next: FeatureVersion = currentVersion === 'classic' ? 'next' : 'classic'
    await dispatch(
      setFeaturePreference({
        featureKey,
        preferredVersion: next,
        restaurantId: effectiveRestaurantId
      })
    ).unwrap()
  }, [currentVersion, dispatch, effectiveRestaurantId, feature, featureKey])

  return {
    currentVersion,
    toggle,
    isLoading: pending,
    error: sliceError
  }
}
